import React from "react";
import { FaFilter, FaTrash } from "react-icons/fa";
import { useTheme } from "../../context/ThemeContext";
import FilterSection from "./FilterSection";
import PriceFilter from "./PriceFilter";
import RatingFilter from "./RatingFilter";

export const FILTER_ARRAY_KEYS = ["category", "typeproduct", "typearticle", "brand", "color", "size"];

export const FILTER_LABELS = {
  category: "Categoria",
  typeproduct: "Producto",
  typearticle: "Tipo Articulo",
  brand: "Marca",
  color: "Color",
  size: "Talla",
  rating: "Rating",
  priceValues: "Precio",
};

export const asFilterArray = value => {
  if (Array.isArray(value)) return value.filter(Boolean);
  return value ? [value] : [];
};

export const hasActiveCatalogFilters = (filters, priceRange) => {
  const hasArrayFilters = FILTER_ARRAY_KEYS.some(key => asFilterArray(filters[key]).length > 0);
  const hasPriceFilter = Boolean(
    priceRange &&
      filters.priceValues &&
      (filters.priceValues[0] !== priceRange.low || filters.priceValues[1] !== priceRange.high)
  );

  return hasArrayFilters || Boolean(filters.rating) || hasPriceFilter;
};

export const getActiveCatalogFilterItems = (filters, priceRange) => {
  const items = [];

  FILTER_ARRAY_KEYS.forEach(key => {
    asFilterArray(filters[key]).forEach(value => {
      items.push({
        key,
        value,
        label: FILTER_LABELS[key],
        text: value,
      });
    });
  });

  if (filters.rating) {
    items.push({
      key: "rating",
      label: FILTER_LABELS.rating,
      text: `${filters.rating} estrellas`,
    });
  }

  if (
    priceRange &&
    filters.priceValues &&
    (filters.priceValues[0] !== priceRange.low || filters.priceValues[1] !== priceRange.high)
  ) {
    items.push({
      key: "priceValues",
      label: FILTER_LABELS.priceValues,
      text: `S/${Math.floor(filters.priceValues[0])} - S/${Math.floor(filters.priceValues[1])}`,
    });
  }

  return items;
};

const FILTER_SECTIONS = [
  { key: "typeproduct", title: "Producto", optionKey: "typeproducts" },
  { key: "typearticle", title: "Tipo Articulo", optionKey: "typearticles" },
  { key: "category", title: "Categoria", optionKey: "categorys" },
  { key: "brand", title: "Marca", optionKey: "brands" },
  { key: "color", title: "Color", optionKey: "colors" },
  { key: "size", title: "Talla", optionKey: "sizes" },
];

export const CatalogActiveFilters = ({
  filters,
  priceRange,
  onRemove,
  onClear,
  title = "Filtros Activos",
  className = "",
}) => {
  const { getColor, getBackground, getTextColor } = useTheme();
  const activeItems = getActiveCatalogFilterItems(filters, priceRange);

  if (!activeItems.length) return null;

  const primaryColor = getColor("primary", "#ae1d22");
  const surfaceColor = getBackground("surface", "#f8f9fa").backgroundColor;
  const borderColor = getColor("border", "#e5e7eb");
  const textSecondary = getTextColor("textSecondary", "#64748b").color;

  return (
    <div
      className={`mb-6 p-4 rounded-lg shadow-sm ${className}`}
      style={{
        backgroundColor: surfaceColor,
        border: `1px solid ${borderColor}`,
      }}>
      <div className="flex justify-between items-center mb-3 gap-3">
        <h3 className="text-sm font-semibold uppercase tracking-wide flex items-center gap-2" style={{ color: textSecondary }}>
          <FaFilter style={{ color: primaryColor }} />
          {title}
        </h3>
        <button
          onClick={onClear}
          className="text-sm font-medium transition-opacity hover:opacity-80 flex items-center gap-1"
          style={{ color: primaryColor }}
          aria-label="Limpiar todos los filtros"
          type="button">
          <FaTrash className="w-3.5 h-3.5" />
          Limpiar todos
        </button>
      </div>
      <div className="flex flex-wrap gap-2">
        {activeItems.map(item => (
          <span
            key={`${item.key}-${item.value || item.text}`}
            className="px-3 py-1.5 text-white text-sm rounded-full flex items-center gap-2 shadow-sm"
            style={{ backgroundColor: primaryColor }}>
            <span className="font-medium">{item.label}:</span> {item.text}
            <button
              onClick={() => onRemove(item.key, item.value)}
              className="ml-1 text-white hover:text-gray-200 text-lg leading-none w-5 h-5 flex items-center justify-center rounded-full transition-colors"
              style={{ backgroundColor: "rgba(255,255,255,0.2)" }}
              aria-label={`Quitar filtro ${item.label} ${item.text}`}
              type="button">
              x
            </button>
          </span>
        ))}
      </div>
    </div>
  );
};

export const CatalogFilterPanel = ({
  filterOptions,
  filters,
  expandedSections,
  toggleSection,
  onCheckboxChange,
  priceRange,
  onPriceChange,
  showPrice = true,
  showRating = false,
  onRatingChange,
  onResetRating,
  idPrefix = "catalog-filter",
  className = "",
}) => {
  const { getColor } = useTheme();
  const primaryColor = getColor("primary", "#ae1d22");

  return (
    <div className={className}>
      {FILTER_SECTIONS.map(section => (
        <FilterSection
          key={section.key}
          title={section.title}
          items={filterOptions[section.optionKey] || []}
          isExpanded={expandedSections[section.key]}
          toggleExpand={() => toggleSection(section.key)}
          checkedValues={asFilterArray(filters[section.key])}
          onChange={onCheckboxChange(section.key)}
          idPrefix={`${idPrefix}-${section.key}`}
          customAccentColor={primaryColor}
        />
      ))}

      {showPrice && (
        <PriceFilter
          priceRange={priceRange}
          priceValues={filters.priceValues}
          onPriceChange={onPriceChange}
          customAccentColor={primaryColor}
        />
      )}

      {showRating && (
        <RatingFilter
          currentRating={filters.rating}
          onRatingChange={onRatingChange}
          onResetRating={onResetRating}
          customAccentColor={primaryColor}
        />
      )}
    </div>
  );
};

export const CatalogFilterToggleButton = ({
  isOpen,
  onClick,
  hasActiveFilters,
  openText = "Ocultar Filtros",
  closedText = "Mostrar Filtros",
  className = "",
}) => {
  const { getButtonStyles, getColor } = useTheme();
  const primaryColor = getColor("primary", "#ae1d22");

  return (
    <button
      onClick={onClick}
      style={{
        ...getButtonStyles("primary", "white", "transparent", primaryColor),
        width: "100%",
        padding: "0.75rem 1rem",
        borderRadius: "0.5rem",
        fontSize: "1rem",
        fontWeight: "500",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "0.5rem",
        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
      }}
      className={className}
      aria-label={isOpen ? openText : closedText}
      type="button">
      <FaFilter />
      {isOpen ? openText : closedText}
      {hasActiveFilters && <span className="w-2 h-2 rounded-full bg-white" />}
    </button>
  );
};
